import {useState} from 'react';
import {BlockStack, Button, Checkbox, InlineStack, Text} from '@shopify/polaris';

export interface AppToggle {
  id: string;
  name: string;
  /** Whether the subject holds this app DIRECTLY (group-inherited access is shown
   *  separately — a group grant is not a direct grant and isn't edited here). */
  granted: boolean;
}

/**
 * The app-grant editor, shared by the per-user and per-group access surfaces.
 *
 * A checkbox per registered app; Save posts the COMPLETE checked set (as repeated
 * `app` fields) and the handler diffs against the subject's current direct grants —
 * so the operation is declarative and idempotent, not a pile of grant/revoke
 * buttons. The hidden `app` inputs are derived from React state, so what posts is
 * exactly what is ticked.
 */
export function AppAccessForm({
  action,
  apps,
  hidden = {},
}: {
  action: string;
  apps: AppToggle[];
  hidden?: Record<string, string>;
}) {
  const [checked, setChecked] = useState<Set<string>>(
    () => new Set(apps.filter((a) => a.granted).map((a) => a.id)),
  );
  const toggle = (id: string, on: boolean) =>
    setChecked((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  return (
    <form method="post" action={action}>
      {Object.entries(hidden).map(([k, v]) => (
        <input key={k} type="hidden" name={k} value={v} />
      ))}
      {[...checked].map((id) => (
        <input key={id} type="hidden" name="app" value={id} />
      ))}
      <BlockStack gap="300">
        {apps.length === 0 ? (
          <Text as="p" tone="subdued">
            No apps are registered.
          </Text>
        ) : (
          apps.map((a) => (
            <Checkbox
              key={a.id}
              label={a.name}
              checked={checked.has(a.id)}
              onChange={(v) => toggle(a.id, v)}
            />
          ))
        )}
        <InlineStack>
          <Button submit variant="primary">
            Save access
          </Button>
        </InlineStack>
      </BlockStack>
    </form>
  );
}
